import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';
import {
  HrBiometricAttendanceImport,
  HrBiometricConnection,
  HrBiometricProviderApp,
  HrBiometricSyncRun
} from './hr-biometric.models';
import { HrBiometricService } from './hr-biometric.service';

@Component({
  selector: 'app-hr-biometric-workspace',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-biometric-workspace.component.html',
  styleUrls: ['./hr-biometric-workspace.component.scss']
})
export class HrBiometricWorkspaceComponent implements OnInit {
  activeListTab = 'connections';
  private readonly biometricService = inject(HrBiometricService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.biometric.manage);

  loading = false;
  connectionDialogVisible = false;
  editingConnectionId: number | null = null;

  providers: HrBiometricProviderApp[] = [];
  connections: HrBiometricConnection[] = [];
  runs: HrBiometricSyncRun[] = [];
  events: HrBiometricAttendanceImport[] = [];
  providerOptions: { label: string; value: string }[] = [];

  connectionForm: {
    providerCode?: string;
    displayName?: string;
    baseUrl?: string;
    accessKey?: string;
    reportPath?: string;
    syncWindowDays?: number;
    snName?: string;
    defaultEventType?: string;
    eventTypeMappingsJson?: string;
    notes?: string;
    active?: boolean;
    defaultConnection?: boolean;
  } = {
    reportPath: 'api/RptTimecard/GetData',
    syncWindowDays: 7,
    active: true,
    defaultConnection: false
  };

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    const imported = this.events.filter(event => event.importStatus === 'IMPORTED').length;
    const failed = this.events.filter(event => event.importStatus === 'FAILED').length;
    const lastRun = this.runs[0];
    return [
      { label: 'Providers', value: this.providers.length, tone: 'info' },
      { label: 'Connections', value: this.connections.length, tone: 'success' },
      { label: 'Imported Punches', value: imported, tone: imported > 0 ? 'success' : 'info' },
      { label: 'Failed Imports', value: failed, tone: failed > 0 ? 'danger' : 'success' },
      { label: 'Last Run', value: lastRun?.status ? this.format(lastRun.status) : 'None', tone: lastRun?.status === 'FAILED' ? 'danger' : 'info' }
    ];
  }

  load(): void {
    this.loading = true;
    forkJoin({
      providers: this.biometricService.marketplaceApps(),
      connections: this.biometricService.connections(),
      runs: this.biometricService.runs(),
      events: this.biometricService.events()
    }).subscribe({
      next: response => {
        this.providers = response.providers;
        this.connections = response.connections;
        this.runs = response.runs;
        this.events = response.events;
        this.providerOptions = response.providers.map(provider => ({
          label: provider.displayName,
          value: provider.providerCode
        }));
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'HR Biometric', detail: 'Unable to load biometric connector data.' });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  openConnection(provider?: HrBiometricProviderApp, connection?: HrBiometricConnection): void {
    const configuration = connection?.configuration || {};
    this.editingConnectionId = connection?.connectionId || null;
    this.connectionForm = {
      providerCode: connection?.providerCode || provider?.providerCode || 'LANATIME',
      displayName: connection?.displayName || provider?.displayName || 'LanaTime',
      baseUrl: connection?.baseUrl || '',
      accessKey: '',
      reportPath: configuration['reportPath'] || 'api/RptTimecard/GetData',
      syncWindowDays: Number(configuration['syncWindowDays'] || 7),
      snName: configuration['snName'] || '',
      defaultEventType: configuration['defaultEventType'] || '',
      eventTypeMappingsJson: configuration['eventTypeMappingsJson'] || '',
      notes: configuration['notes'] || '',
      active: connection?.active ?? true,
      defaultConnection: connection?.defaultConnection ?? false
    };
    this.connectionDialogVisible = true;
  }

  onProviderChanged(providerCode: string): void {
    if (providerCode === 'LANATIME' && !this.connectionForm.reportPath) {
      this.connectionForm.reportPath = 'api/RptTimecard/GetData';
    }
  }

  saveConnection(): void {
    if (!this.canManage || !this.connectionForm.providerCode || !this.connectionForm.displayName) {
      return;
    }
    const configuration: Record<string, any> = {
      reportPath: this.connectionForm.reportPath || 'api/RptTimecard/GetData',
      syncWindowDays: this.connectionForm.syncWindowDays || 7
    };
    this.putIfPresent(configuration, 'snName', this.connectionForm.snName);
    this.putIfPresent(configuration, 'defaultEventType', this.connectionForm.defaultEventType);
    this.putIfPresent(configuration, 'eventTypeMappingsJson', this.connectionForm.eventTypeMappingsJson);
    this.putIfPresent(configuration, 'notes', this.connectionForm.notes);

    const credentials: Record<string, any> = {};
    this.putIfPresent(credentials, 'accesskey', this.connectionForm.accessKey);

    const payload: HrBiometricConnection = {
      connectionId: this.editingConnectionId || undefined,
      providerCode: this.connectionForm.providerCode,
      displayName: this.connectionForm.displayName,
      baseUrl: this.connectionForm.baseUrl || undefined,
      active: this.connectionForm.active ?? true,
      defaultConnection: this.connectionForm.defaultConnection ?? false,
      credentials,
      configuration
    };
    this.biometricService.saveConnection(payload).subscribe({
      next: () => {
        this.connectionDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'HR Biometric', detail: 'Biometric connection saved.' });
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'HR Biometric', detail: 'Unable to save biometric connection.' });
      }
    });
  }

  test(connection: HrBiometricConnection): void {
    if (!connection.connectionId) {
      return;
    }
    this.biometricService.testConnection(connection.connectionId).subscribe({
      next: run => {
        this.messageService.add({ severity: 'success', summary: 'HR Biometric', detail: `Test run ${run.runId || ''} started.` });
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'HR Biometric', detail: 'Unable to test biometric connection.' });
      }
    });
  }

  sync(connection: HrBiometricConnection): void {
    if (!connection.connectionId) {
      return;
    }
    this.biometricService.syncConnection(connection.connectionId).subscribe({
      next: run => {
        this.messageService.add({ severity: 'success', summary: 'HR Biometric', detail: `Sync run ${run.runId || ''} started.` });
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'HR Biometric', detail: 'Unable to sync biometric connection.' });
      }
    });
  }

  format(value?: string): string {
    return (value || '-')
      .toLowerCase()
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  statusSeverity(value?: string): 'success' | 'warn' | 'danger' | 'info' {
    switch ((value || '').toUpperCase()) {
      case 'COMPLETED':
      case 'IMPORTED':
      case 'ACTIVE':
        return 'success';
      case 'FAILED':
        return 'danger';
      case 'RUNNING':
      case 'PENDING':
        return 'warn';
      default:
        return 'info';
    }
  }

  private putIfPresent(target: Record<string, any>, key: string, value: any): void {
    if (value === null || value === undefined || String(value).trim() === '') {
      return;
    }
    target[key] = value;
  }
}
